import { useEffect, useState, useRef } from "react";
import StepProgress from "../components/website-Generator/StepProgress";
import BusinessDetailsStep from "../components/website-Generator/BusinessDetailsStep";
import ThemeSelectionStep from "../components/website-Generator/ThemeSelectionStep";
import ReviewStep from "../components/website-Generator/ReviewStep";
import ActionBar from "../components/website-Generator/ActionBar";
import {
    createAndQueueProject,
    getPreview,
    publishProject,
    autofillWebsiteForm,
    getThemes
} from "../services/websiteService";
import "./GenerateWebsitePage.css";

const fallbackThemes = [
    { key: "clean-light", name: "Clean Light", description: "Modern clean business layout" },
    { key: "modern-glass", name: "Modern Glass", description: "Glassmorphism futuristic UI" },
    { key: "premium-dark", name: "Premium Dark", description: "Dark luxury professional layout" },
    { key: "luxury-light", name: "Luxury Light", description: "Elegant light premium feel" },
    { key: "bold-gradient", name: "Bold Gradient", description: "Vibrant modern tech UI" }
];

const getRecommendedPages = (industry, subIndustry) => {
    const text = `${industry} ${subIndustry}`.toLowerCase();

    if (text.includes("sports") || text.includes("academy") || text.includes("training")) {
        return ["Home", "Programs", "About", "Contact"];
    }

    if (text.includes("ecommerce") || text.includes("store") || text.includes("shop")) {
        return ["Home", "Shop", "Collections", "Contact"];
    }

    if (text.includes("education") || text.includes("course") || text.includes("school")) {
        return ["Home", "Programs", "Admissions", "Contact"];
    }

    return ["Home", "About", "Services", "Contact"];
};

export default function GenerateWebsitePage() {
    const [step, setStep] = useState(1);
    const [themes, setThemes] = useState(fallbackThemes);

    const [loading, setLoading] = useState(false);
    const [autofilling, setAutofilling] = useState(false);
    const [publishing, setPublishing] = useState(false);

    const [projectId, setProjectId] = useState("");
    const [status, setStatus] = useState("");
    const [liveUrl, setLiveUrl] = useState("");
    const [previewHtml, setPreviewHtml] = useState("");

    const pageAutoFilledRef = useRef(false);

    const [form, setForm] = useState({
        businessName: "",
        industry: "",
        subIndustry: "",
        description: "",

        tone: "Professional",
        themeKey: "clean-light",

        primaryAudience: "",
        secondaryAudience: "",
        location: "",
        serviceAreas: "",

        goalOfWebsite: "",
        callToActionType: "",
        brandStyle: "",
        pricePositioning: "",

        coreServices: "",
        projectTypes: "",
        uniqueSellingPoints: "",
        trustSignals: "",
        yearsInBusiness: "",
        mustIncludeWords: "",
        avoidWords: "",

        competitorWebsiteUrl: "",
        competitorStyleNotes: "",
        desiredLayoutStyle: "",
        visualTone: "",
        preferredSectionDensity: "",
        referenceWebsiteType: "",

        // page selection
        pages: ["Home", "About", "Services", "Contact"],

        // NEW generic repeatable business offerings
        offeredItems: [
            {
                id: crypto.randomUUID(),
                name: "",
                subtitle: "",
                description: "",
                badge: "",
                category: "",
                audience: "",
                level: "",
                imagePreference: "",
                keyPoints: [""]
            }
        ]
    });

    useEffect(() => {
        if (!form.industry && !form.subIndustry) return;
        if (pageAutoFilledRef.current) return;

        const recommended = getRecommendedPages(form.industry, form.subIndustry);

        setForm((prev) => ({
            ...prev,
            pages: (prev.pages && prev.pages.length > 0)
                ? prev.pages
                : recommended
        }));

        pageAutoFilledRef.current = true;

    }, [form.industry, form.subIndustry]);

    useEffect(() => {
        const loadThemes = async () => {
            try {
                const res = await getThemes();
                if (Array.isArray(res) && res.length > 0) {
                    setThemes(res);
                }
            } catch {
                setThemes(fallbackThemes);
            }
        };

        loadThemes();
    }, []);

    const updateField = (name, value) => {
        setForm((prev) => ({
            ...prev,
            [name]: value
        }));

        if (name === "industry" || name === "subIndustry") {
            pageAutoFilledRef.current = false;
        }
    };

    const mergeAutofillData = (data) => {
        setForm((prev) => ({
            ...prev,
            businessName: data.businessName || prev.businessName,
            industry: data.industry || prev.industry,
            subIndustry: data.subIndustry || prev.subIndustry,
            description: data.description || prev.description,
            tone: data.tone || prev.tone,
            themeKey: isValidThemeKey(data.themeKey, themes) ? data.themeKey : prev.themeKey,
            pages: Array.isArray(data.pages) && data.pages.length > 0 ? data.pages : prev.pages,

            primaryAudience: data.primaryAudience || prev.primaryAudience,
            secondaryAudience: data.secondaryAudience || prev.secondaryAudience,
            location: data.location || prev.location,
            serviceAreas: data.serviceAreas || prev.serviceAreas,
            coreServices: data.coreServices || prev.coreServices,
            projectTypes: data.projectTypes || prev.projectTypes,
            uniqueSellingPoints: data.uniqueSellingPoints || prev.uniqueSellingPoints,
            trustSignals: data.trustSignals || prev.trustSignals,
            yearsInBusiness: data.yearsInBusiness || prev.yearsInBusiness,
            pricePositioning: data.pricePositioning || prev.pricePositioning,
            goalOfWebsite: data.goalOfWebsite || prev.goalOfWebsite,
            callToActionType: data.callToActionType || prev.callToActionType,
            brandStyle: data.brandStyle || prev.brandStyle,
            competitorStyle: data.competitorStyle || prev.competitorStyle,
            mustIncludeWords: data.mustIncludeWords || prev.mustIncludeWords,
            avoidWords: data.avoidWords || prev.avoidWords,

            competitorWebsiteUrl: data.competitorWebsiteUrl || prev.competitorWebsiteUrl,
            competitorStyleNotes: data.competitorStyleNotes || prev.competitorStyleNotes,
            desiredLayoutStyle: data.desiredLayoutStyle || prev.desiredLayoutStyle,
            visualTone: data.visualTone || prev.visualTone,
            preferredSectionDensity: data.preferredSectionDensity || prev.preferredSectionDensity,
            referenceWebsiteType: data.referenceWebsiteType || prev.referenceWebsiteType
        }));
    };

    const isValidThemeKey = (key, themeList) => {
        return themeList.some((t) => t.key === key);
    };

    const handleAutofill = async () => {
        if (!form.businessName || !form.industry || !form.description) {
            alert("Enter Business Name, Industry, and Description first.");
            return;
        }

        try {
            setAutofilling(true);

            const data = await autofillWebsiteForm({
                businessName: form.businessName,
                industry: form.industry,
                description: form.description
            });

            mergeAutofillData(data);
            alert("AI autofill completed.");
        } catch (error) {
            console.error(error);
            alert("AI autofill failed.");
        } finally {
            setAutofilling(false);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setPreviewHtml("");
            setLiveUrl("");

            const res = await createAndQueueProject(form);
            setProjectId(res.projectId || "");
            setStatus(res.status || "Submitted");

            if (res.projectId) {
                pollProjectStatus(res.projectId);
            }
        } catch (error) {
            console.error(error);
            alert("Failed to save project.");
        } finally {
            setLoading(false);
        }
    };

    const pollProjectStatus = (id) => {
        const interval = setInterval(async () => {
            try {
                const preview = await getPreview(id);
                setStatus(preview.status || "");

                if (preview.status === "Generated") {
                    clearInterval(interval);
                    setPreviewHtml(preview.html || "");
                    setLiveUrl(preview.liveUrl || "");
                }

                if (preview.status === "Failed") {
                    clearInterval(interval);
                    alert("Website generation failed.");
                }
            } catch (error) {
                clearInterval(interval);
                console.error(error);
            }
        }, 3000);
    };

    const handlePreview = async () => {
        if (!projectId) {
            alert("Save project first.");
            return;
        }

        try {
            setLoading(true);
            const res = await getPreview(projectId);
            setPreviewHtml(res.html || "");
            setStatus(res.status || "");
            setLiveUrl(res.liveUrl || "");
        } catch (error) {
            console.error(error);
            alert("Failed to load preview.");
        } finally {
            setLoading(false);
        }
    };

    const handlePublish = async () => {
        if (!projectId) {
            alert("Save project first.");
            return;
        }

        try {
            setPublishing(true);
            const res = await publishProject(projectId);
            if (res.success) {
                setLiveUrl(res.liveUrl || "");
                alert("Website published successfully.");
            } else {
                alert("Publish failed.");
            }
        } catch (error) {
            console.error(error);
            alert("Failed to publish website.");
        } finally {
            setPublishing(false);
        }
    };

    return (
        <div className="wg-page">
            <div className="wg-shell">
                <div className="wg-header">
                    <div>
                        <div className="wg-badge">Shriansh Nexus</div>
                        <h1 className="wg-title">AI Website Generator</h1>
                        <p className="wg-subtitle">
                            Fill your business details, let AI enrich the content, choose a theme,
                            then save, preview, and publish.
                        </p>
                    </div>
                </div>

                <StepProgress step={step} />

                <div className="wg-card">
                    {step === 1 && (
                        <BusinessDetailsStep
                            form={form}
                            updateField={updateField}
                            onAutofill={handleAutofill}
                            autofilling={autofilling}
                        />
                    )}

                    {step === 2 && (
                        <ThemeSelectionStep
                            themes={themes}
                            form={form}
                            updateField={updateField}
                        />
                    )}

                    {step === 3 && (
                        <ReviewStep
                            form={form}
                            projectId={projectId}
                            status={status}
                            liveUrl={liveUrl}
                        />
                    )}
                </div>

                <ActionBar
                    step={step}
                    loading={loading}
                    publishing={publishing}
                    onBack={() => setStep((s) => Math.max(1, s - 1))}
                    onNext={() => setStep((s) => Math.min(3, s + 1))}
                    onSave={handleSave}
                    onPreview={handlePreview}
                    onPublish={handlePublish}
                />

                {previewHtml && (
                    <div className="wg-preview-card">
                        <div className="wg-preview-head">
                            <div>
                                <span className="wg-preview-badge">Preview</span>
                                <span className="wg-preview-theme">{form.themeKey}</span>
                            </div>

                            {liveUrl && (
                                <a
                                    href={liveUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="wg-live-link"
                                >
                                    Open Live Site
                                </a>
                            )}
                        </div>

                        <iframe
                            title="Website Preview"
                            srcDoc={previewHtml}
                            className="wg-preview-frame"
                        />
                    </div>
                )}
            </div>
        </div>
    );
}